import { getExtensionListForSSR } from '@atlassian/forge-ui/provider';

import { query, getPreloaderFnContext } from '@confluence/query-preloader-tools';
import { AccessStatus } from '@confluence/session-data';
import { preloadSpaceDetail } from '@confluence/space-utils';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities';

import { preloadUseStaticContext } from '../display-conditions/preloadUseStaticContext';
import { filterExtensionsByDisplayConditions } from '../display-conditions/useExtensionsFilteredByDisplayConditions';
import { fg } from '@confluence/feature-gating';

import { supportsAccessNarrowing, type UseExtensionListProps } from './useExtensionList';
import { getUseExtensionListOptions } from './getUseExtensionListOptions';
import { getExtensions } from './getExtensions';

export const filterExtensions = async (
	staticContext,
	extensions,
	contentId,
	spaceKey,
	moduleType,
) => {
	const fetchContext = { contentId, spaceKey };
	const filteredResult = await filterExtensionsByDisplayConditions(
		extensions,
		staticContext,
		fetchContext,
	);
	window.__SSR_FORGE_EXTENSIONS__ = window.__SSR_FORGE_EXTENSIONS__ || {};
	window.__SSR_FORGE_EXTENSIONS__[moduleType] = {
		extensions: filteredResult.extsToDisplay,
		error: filteredResult.conditionsError,
	};
};

export async function preloadUseExtensionList({
	contentId,
	spaceKey,
	moduleType,
	queryOptions,
	expandAppOwner = false,
	skipFilter = false,
}: UseExtensionListProps & { contentId?: string; spaceKey: string; skipFilter?: boolean }) {
	const { cloudId, activationId, isLoggedIn, accessStatus, locale } = await getPreloaderFnContext();

	const hasAccessNarrowingSupport = supportsAccessNarrowing(moduleType);
	const isAnonymous = !isLoggedIn;
	const isExternalCollaborator = accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;

	try {
		if (fg('optimize_extension_list_call')) {
			const [staticContext, extensions] = await Promise.all([
				preloadUseStaticContext({ spaceKey }),
				getExtensions({
					spaceKey: spaceKey,
					cloudId: cloudId,
					activationId: activationId,
					locale: locale,
					hasAccessNarrowingSupport: hasAccessNarrowingSupport,
					expandAppOwner: expandAppOwner,
					moduleType: moduleType,
					queryOptions: queryOptions,
					skip: isAnonymous || isExternalCollaborator,
					skipFilter: skipFilter,
				}),
			]);

			if (extensions) {
				await filterExtensions(staticContext, extensions, contentId, spaceKey, moduleType);
			}
		} else {
			const [staticContext, { data }] = await Promise.all([
				preloadUseStaticContext({ spaceKey }),
				preloadSpaceDetail(spaceKey),
			]);
			const dataClassificationTags = data?.space?.dataClassificationTags;

			const extensions = await getExtensionListForSSR({
				...getUseExtensionListOptions({
					cloudId,
					activationId,
					locale,
					dataClassificationTags,
					hasAccessNarrowingSupport,
					expandAppOwner,
					moduleType,
					queryOptions,
					skip: isAnonymous || isExternalCollaborator,
					skipFilter,
				}),
				queryFn: query,
			});

			if (extensions) {
				await filterExtensions(staticContext, extensions, contentId, spaceKey, moduleType);
			}
		}
	} catch (e) {
		ssrPrepareErrorsClient.submitError(
			new Error(`Filed to get forge extension list: ${e.message}`),
		);
	}
}
